import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/hooks/useAuth';
import { t } from '@/i18n/translations';
import { formatApiError } from '@/lib/apiError';
import { AuthShell } from '@/components/layout/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user, lang } = useAuth();
  const navigate = useNavigate();

  if (user) {
    navigate('/');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(identifier.trim(), password);
      toast.success(t('login_success', lang));
      navigate('/');
    } catch (err) {
      toast.error(formatApiError(err, t('login_error', lang)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell lang={lang} title={t('login', lang)} description={t('login_subtitle', lang)}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="identifier">{t('email_or_username', lang)}</Label>
          <Input
            id="identifier"
            type="text"
            autoComplete="username"
            autoFocus
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder={t('email_or_username_placeholder', lang)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">{t('password', lang)}</Label>
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-primary hover:underline"
            >
              {t('forgot_password', lang)}
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />
        </div>
        <Button type="submit" className="w-full" disabled={loading} size="lg">
          {loading ? t('entering', lang) : t('login', lang)}
        </Button>
      </form>
    </AuthShell>
  );
}
